/* react/jsx-props-no-spreading */
import { useEffect, useMemo, useState } from "react";
import type { MRT_DensityState, MRT_RowData } from "material-react-table";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";

import { usePrevious } from "@hooks";

import type { UtfTableProps } from "./utils";
import {
  handlePaste,
  mergeDefaultColumn,
  rowsPerPageOptions,
  useDefaultTableOptions,
} from "./utils";

/**
 * A base component to construct rich datatables
 *
 * This is a wrapper component for using Material React Table with defaults of apps needs.
 *
 * `columns` and `data` props are the only required props, but there are over 170 other optional props.
 *
 * See more info on creating columns and data on the official docs site:
 * @link https://www.material-react-table.com/docs/getting-started/usage
 *
 * See the full props list on the official docs site:
 * @link https://www.material-react-table.com/docs/api/props
 */
function UtfTable<TData extends MRT_RowData>({
  onDataChange = () => {}, // eslint-disable-next-line @typescript-eslint/no-empty-function
  onCellChange = () => {}, // eslint-disable-next-line @typescript-eslint/no-empty-function
  // material-react-table props
  data,
  columns,
  enableEditing = false,
  editDisplayMode = "row",
  initialState,
  state,
  displayColumnDefOptions,
  muiTablePaperProps,
  ...initialProps
}: Readonly<UtfTableProps<TData>>) {
  // Density state
  const [density, setDensity] = useState<MRT_DensityState>(state?.density ?? "comfortable");
  // FullScreen state
  const [isFullScreen, setIsFullScreen] = useState<boolean>(Boolean(state?.isFullScreen));
  const [portalEl, setPortalEl] = useState<DocumentFragment | HTMLElement | null>(null);
  // Conditional column virtualization state
  const [enableColumnVirtualization, setEnableColumnVirtualization] = useState<boolean>(false);

  // Populate the theme and default table options
  const defaultProps = useDefaultTableOptions<TData>(initialProps, {
    rowCount: (data || []).length,
    isFullScreen,
  });

  // Table state must be stable
  const tableState = useMemo(
    () => ({
      ...state,
      density,
      isFullScreen,
    }),
    [density, isFullScreen, state]
  );

  // Sanitize columns to ensure no functions are used as headers
  const sanitizedColumns = useMemo(() => {
    return (columns || []).map((column) => ({
      ...column,
      header: typeof column.header === "function" ? "" : column.header,
    }));
  }, [columns]);

  // Initialize Material React Table
  const table = useMaterialReactTable<TData>({
    data,
    columns: sanitizedColumns,
    initialState: {
      expanded: true,
      pagination: { pageIndex: 0, pageSize: rowsPerPageOptions[0] },
      ...initialState,
    },
    displayColumnDefOptions: {
      ...{ ...(displayColumnDefOptions ?? {}) },
      "mrt-row-select": {
        size: 56,
        minSize: 56,
        muiTableBodyCellProps: { align: "center", sx: { px: 2 } },
        muiTableHeadCellProps: { align: "center", sx: { px: 2 } },
        header: "", // Explicitly set to empty string to avoid function
        ...{ ...(displayColumnDefOptions?.["mrt-row-select"] ?? {}) },
      },
      "mrt-row-expand": {
        size: 50,
        minSize: 50,
        maxSize: 50,
        header: "", // Explicitly set to empty string to avoid function
        ...{ ...(displayColumnDefOptions?.["mrt-row-expand"] ?? {}) },
      },
      "mrt-row-actions": {
        header: "", // Explicitly set to empty string to avoid function
        ...{ ...(displayColumnDefOptions?.["mrt-row-actions"] ?? {}) },
      },
    },
    state: tableState,
    onDensityChange: setDensity,
    onIsFullScreenChange: setIsFullScreen,

    // get merged defaults from user-provided and the theme props
    ...defaultProps,

    // After ...defaultProps so caller defaultColumn cannot drop sortUndefined
    defaultColumn: mergeDefaultColumn(defaultProps.defaultColumn),

    muiTablePaperProps: {
      ...muiTablePaperProps,
      ...defaultProps.muiTablePaperProps,
    },

    // props that can NOT be overriden by the user-provied props
    enableColumnVirtualization,
    enableEditing,
    editDisplayMode,
    muiEditTextFieldProps: ({ cell, table: tableInstance }: any) => ({
      onBlur: (event: any) => {
        // onBlur is more efficient, but could use onChange instead
        onCellChange(cell.row.original, cell.column.id, event.target.value);
      },
      onPaste: (event: any) => {
        handlePaste(event, cell, tableInstance, data, onDataChange);
      },
    }),
  });

  const materialReactTable = <MaterialReactTable table={table} />;

  // Nested columns are causing crash with `enableColumnVirtualization` prop set to `true`
  // This workaround will disable the virtualization if any of the nested columns are enabled
  const visibleColumns = table.getVisibleLeafColumns();
  const visibleColumnCount = visibleColumns.length;
  const prevVisibleColumnCount = usePrevious(visibleColumnCount);
  useEffect(() => {
    if (!prevVisibleColumnCount || prevVisibleColumnCount === visibleColumnCount) return;
    setEnableColumnVirtualization(
      !visibleColumns.some(({ depth }: { depth: number }) => depth !== 0)
    );
  }, [visibleColumns, table, prevVisibleColumnCount, visibleColumnCount]);

  return materialReactTable;
}

export default UtfTable;
